import type { SiteAuditEntry } from '@mg/shared';
import { describeAiModelsAudit } from './aiModels';
import { describeSocialAppAudit } from './socialMediaIntegrations';

/**
 * Site Integrations → "Recent site changes": one line per install-level change. Moved here unchanged from
 * SiteIntegrationsPage.tsx, which the react-refresh lint rule limits to exporting components, so the
 * wiring the page uses can be tested on its own (siteAudit.test.ts). The AI models card's saves come
 * first, then the social app keys, each with its own sentence.
 */

/** Turn a raw audit action + entity into a human-readable phrase. */
export function describeSiteAudit(entry: SiteAuditEntry): string {
  const ai = describeAiModelsAudit(entry);
  if (ai !== null) return ai;
  const social = describeSocialAppAudit(entry);
  if (social !== null) return social;
  const provider =
    typeof entry.diff.provider === 'string' ? ` — ${entry.diff.provider}` : '';
  switch (entry.action) {
    case 'platform-settings.update': {
      const fields = Object.keys(entry.diff);
      return fields.length
        ? `Updated site settings (${fields.join(', ')})`
        : 'Updated site settings';
    }
    case 'media-credential.save':
      return `Saved media credential${provider}`;
    case 'media-credential.remove':
      return `Removed media credential${provider}`;
    case 'user.tenant-admin.grant':
      return 'Granted Tenant Admin';
    case 'user.tenant-admin.revoke':
      return 'Revoked Tenant Admin';
    default:
      return `${entry.action} (${entry.entity})`;
  }
}
